import { readRun, readWeights } from "../index.js";
import type { BlockWire } from "../types.js";

/**
 * Whether two texts give a phone the same workout (DESIGN.md §25.4): the weights reading and the
 * run reading, exercise by exercise. A note may carry more of his words than before, never fewer.
 */
export function readsSame(before: string[], after: string[]): boolean {
  return readingDifferences(before, after).length === 0;
}

export function readingDifferences(before: string[], after: string[]): string[] {
  const a = meaning(before);
  const b = meaning(after);
  const out: string[] = [];
  for (const which of ["weights", "runs"] as const) {
    const x = a[which];
    const y = b[which];
    for (let i = 0; i < Math.max(x.length, y.length); i++) {
      if (x[i] === y[i]) continue;
      if (x[i] !== undefined && y[i] !== undefined) {
        const [xf, xn] = split(x[i]!);
        const [yf, yn] = split(y[i]!);
        if (xf === yf && xn.every((w) => yn.includes(w))) continue;
      }
      out.push(`${which}: ${x[i] ?? "(none)"}  →  ${y[i] ?? "(none)"}`);
    }
  }
  return out;
}

function meaning(lines: string[]): { weights: string[]; runs: string[] } {
  return { weights: describe(readWeights(lines.join("\n")).plan.blocks), runs: describe(readRun(lines).blocks) };
}

function describe(blocks: BlockWire[]): string[] {
  return blocks.flatMap((b, bi) =>
    b.exercises.map((e) => {
      const target = e.target ? `${e.target.amount}${e.target.upper !== undefined ? `-${e.target.upper}` : ""}${e.target.unit[0]}` : "-";
      const facts = [
        `b${bi}`,
        `${b.sets}x`,
        b.isPrep ? "prep" : "",
        target,
        e.name.toLowerCase(),
        e.perSide ? "each side" : "",
        e.isBodyweight ? "bw" : "",
        e.isResisted ? "resisted" : "",
        e.plannedLoad !== undefined ? `${e.plannedLoad}kg` : "",
        e.parts ? e.parts.join("+") : "",
        e.groups !== undefined ? `g${e.groups}` : "",
      ].filter((f) => f !== "");
      // The round's note rides on each of its exercises, under the same rule: more words, never fewer.
      return `${facts.join(" ")} ⟨${words([b.note ?? "", e.note ?? ""].join(" ")).join(" ")}⟩`;
    }),
  );
}

// Words a note may gain or lose without saying anything different: how rest and a surface are spelled.
const quiet = new Set(["rest", "break", "r", "from", "on", "the", "with", "and", "a", "min", "s"]);

function words(text: string): string[] {
  const all = text.toLowerCase().split(/[^\p{L}\p{N}%.]+/u);
  return [...new Set(all.filter((w) => w !== "" && !quiet.has(w)))].sort();
}

function split(line: string): [string, string[]] {
  const at = line.indexOf(" ⟨");
  return [line.slice(0, at), line.slice(at + 2, -1).split(" ").filter((w) => w !== "")];
}
